import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { rpc } from "./rpc";
import type { Comment } from "./comments";
import type { Doc, DocumentUser } from "./types";

/**
 * The in-app inbox (`docs/spec/notifications.md` §9).
 *
 * Every page of `notifications.list` carries the unseen count alongside it, so
 * the badge never needs a second request. The list is polled rather than pushed:
 * a minute is well inside "I have a new mention" latency and costs one small
 * query per open tab.
 */

export interface Notification {
  id: string;
  event: string;
  userId: string;
  actorId: string | null;
  actor: DocumentUser | null;
  documentId: string | null;
  /** Omitted when the subject has been deleted or is no longer readable. */
  document?: Doc;
  collectionId: string | null;
  commentId: string | null;
  comment?: Comment;
  revisionId: string | null;
  groupId: string | null;
  membershipId: string | null;
  viewedAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  data: Record<string, unknown> | null;
}

export interface NotificationPage {
  notifications: Notification[];
  unseen: number;
}

export const POLL_INTERVAL_MS = 60_000;

export const notificationKeys = {
  list: (archived: boolean) => ["notifications.list", archived] as const,
};

export function useNotifications(
  archived: boolean,
  enabled = true,
): UseQueryResult<NotificationPage> {
  return useQuery({
    queryKey: notificationKeys.list(archived),
    enabled,
    retry: false,
    // A minute of latency on an inbox badge is invisible; a shorter poll is not
    // worth the requests, and the window regaining focus covers the rest.
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const result = await rpc<NotificationPage>("notifications.list", {
        archived,
        limit: 25,
      });
      return { notifications: result.data.notifications, unseen: result.data.unseen };
    },
  });
}

function useInvalidateInbox(): () => void {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: ["notifications.list"], exact: false });
  };
}

export interface NotificationUpdate {
  id: string;
  viewedAt?: string | null;
  archivedAt?: string | null;
}

export function useUpdateNotification(): UseMutationResult<
  Notification,
  Error,
  NotificationUpdate
> {
  const invalidate = useInvalidateInbox();
  return useMutation({
    mutationFn: async (input: NotificationUpdate) =>
      (await rpc<Notification>("notifications.update", input)).data,
    onSuccess: invalidate,
  });
}

/**
 * "Mark all read". Setting `viewedAt` touches only the rows where it is null, so
 * pressing it twice is free.
 */
export function useUpdateAllNotifications(): UseMutationResult<
  { total: number },
  Error,
  { viewedAt?: string; archivedAt?: string }
> {
  const invalidate = useInvalidateInbox();
  return useMutation({
    mutationFn: async (input: { viewedAt?: string; archivedAt?: string }) =>
      (await rpc<{ total: number }>("notifications.update_all", input)).data,
    onSuccess: invalidate,
  });
}
